import WhatsNewBell from "@/shared/components/WhatsNew/WhatsNewBell";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { renderCampaignCell } from "@/shared/utils/utils";
import { Copy, Download, Pen, Trash } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { buildURLFromFilters } from "@/shared/utils/filterUrl";
import { sanitizeFilename, triggerDownload } from "@/shared/utils/tableExport";
import { showError } from "@/shared/utils/toast";
import { exportCampaign } from "@/features/campaigns/api/campaignsApi";
import NewCampaignModal from "./components/NewCampaignModal";
import {
  useDeleteCampaign,
  useBulkDeleteCampaigns,
  useDuplicateCampaign,
  useGetCampaigns,
} from "@/features/campaigns/hooks/useCampaign";
import Loader from "@/shared/components/ui/Loaders/Loader";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import NoDataTable from "@/shared/components/ui/NoDataView/NoDataTable";
import noCampaigns from "@/assets/images/light_bulb.gif";
import NoDataCommonCard from "@/shared/components/ui/NoDataView/NoDataCommon";
import CampaignHeader from "./components/CampaignHeader";
import CampaignTable from "./components/CampaignTable";

export default function Campaigns() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInternalURLUpdate = useRef(false);
  const isReadingFromURL = useRef(false);
  const isFirstRender = useRef(true);

  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [deleteConfirmKind, setDeleteConfirmKind] = useState(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign({
    action: () => {
      setDeleteConfirmKind(null);
      setDeletingCampaignId(null);
    },
  });
  const { mutate: duplicateCampaign } = useDuplicateCampaign();
  const { mutate: bulkDeleteCampaigns, isPending: isBulkDeleting } =
    useBulkDeleteCampaigns({
      onSuccess: () => {
        setDeleteConfirmKind(null);
        setSelectedCampaigns([]);
      },
    });

  const handleEdit = useCallback((id) => {
    setEditingCampaignId(id);
    setShowNewCampaignModal(true);
  }, []);

  const handleDelete = useCallback((id) => {
    setDeletingCampaignId(id);
    setDeleteConfirmKind("single");
  }, []);

  const handleDuplicate = useCallback((id) => {
    duplicateCampaign({ campaignId: id });
  }, [duplicateCampaign]);

  const handleDownload = useCallback(async (id) => {
    try {
      const file = await exportCampaign(id);
      triggerDownload(
        new Blob([JSON.stringify(file, null, 2)], { type: "application/json" }),
        `${sanitizeFilename(file.campaign.campaignName)}.campaign.json`,
      );
    } catch (error) {
      showError(error.message);
    }
  }, []);

  const handleImported = useCallback((campaign) => {
    setShowNewCampaignModal(false);
    setEditingCampaignId(null);
    navigate(`/campaigns/${campaign.id}/campaignsteps`);
  }, [navigate]);

  const handleDeleteCampaign = useCallback(() => {
    deleteCampaign({ campaignId: deletingCampaignId });
  }, [deleteCampaign, deletingCampaignId]);

  const handleCloseModal = useCallback(() => {
    setShowNewCampaignModal(false);
    setEditingCampaignId(null);
  }, []);

  const actions = useMemo(() => {
    return [
      {
        id: "edit",
        name: "Edit",
        onClick: handleEdit,
        icon: <Pen size={16} className="text-gray-700 dark:text-gray-300" />,
      },
      {
        id: "duplicate",
        name: "Duplicate",
        onClick: handleDuplicate,
        icon: <Copy size={16} className="text-gray-700 dark:text-gray-300" />,
      },
      {
        id: "download",
        name: "Download",
        onClick: handleDownload,
        icon: <Download size={16} className="text-gray-700 dark:text-gray-300" />,
      },
      {
        id: "delete",
        name: "Delete",
        onClick: handleDelete,
        icon: <Trash size={16} color="#ef4444" />,
      },
    ];
  }, [handleEdit, handleDelete, handleDuplicate, handleDownload]);

  const {
    data: campaignsData,
    isLoading,
    isFetching: isFetchingCampaigns,
  } = useGetCampaigns(currentPage, rowsPerPage, searchValue, true);

  const campaignsFlat = campaignsData?.campaigns ?? [];
  const totalCount = campaignsData?.totalCount ?? 0;

  // Transform campaigns data for table
  const displayData = useMemo(() => {
    return campaignsFlat?.map((campaign) => ({
      id: campaign?.id,
      name: campaign?.name || campaign?.campaignName || "",
      created_on: campaign?.created_at
        ? new Date(campaign?.created_at).toLocaleDateString()
        : "",
      sent: campaign?.sent_count || campaign?.sent || 0,
      clicked: campaign?.clicked_count || campaign?.clicked || 0,
      opened: campaign?.opened_count || campaign?.opened || 0,
      status: campaign?.status || "Draft",
      start_schedule:
        campaign?.start_schedule ?? campaign?.startSchedule ?? null,
    }));
  }, [campaignsFlat]);

  const totalPages =
    rowsPerPage > 0 ? Math.max(1, Math.ceil(totalCount / rowsPerPage)) : 1;

  useEffect(() => {
    if (isFetchingCampaigns) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, isFetchingCampaigns]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, rowsPerPage]);

  // ── URL  ──
  useEffect(() => {
    if (isInternalURLUpdate.current) {
      isInternalURLUpdate.current = false;
      return;
    }
    isReadingFromURL.current = true;
    const urlSearch = searchParams.get("search") || "";
    setSearchValue(urlSearch);
  }, [searchParams]);

  // ── State → URL ──
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isReadingFromURL.current) {
      isReadingFromURL.current = false;
      return;
    }
    isInternalURLUpdate.current = true;
    const queryString = buildURLFromFilters({
      filters: {},
      searchValue,
    });
    setSearchParams(queryString, { replace: true });
  }, [searchValue, setSearchParams]);

  // Reset selection when search changes
  const prevSearchRef = useRef(searchValue);
  useEffect(() => {
    if (prevSearchRef.current !== searchValue && selectedCampaigns.length > 0) {
      setSelectedCampaigns([]);
    }
    prevSearchRef.current = searchValue;
  }, [searchValue, selectedCampaigns.length]);

  const onSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleNewCampaign = () => {
    setShowNewCampaignModal(true);
  };

  useEffect(() => {
    if (routerLocation.state?.openNewCampaign) {
      setEditingCampaignId(null);
      setShowNewCampaignModal(true);
      navigate(routerLocation.pathname + routerLocation.search, {
        replace: true,
        state: {},
      });
    }
  }, [
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.state,
    navigate,
  ]);

  const hasSearchOrFilters = searchValue.trim().length > 0;
  const viewState = (() => {
    const hasSearch = searchValue.trim().length > 0;
    const hasData = displayData?.length > 0;

    if (isLoading) return "LOADING";

    if (!hasData && !hasSearch) {
      return "EMPTY_INITIAL";
    }

    if (!hasData && hasSearch) {
      return "EMPTY_SEARCH";
    }

    return "DATA";
  })();

  const handleSelectionChange = useCallback((campaigns) => {
    const minimal = campaigns.map((c) => ({
      id: c.id,
      name: c.name || c.campaignName || "",
    }));
    setSelectedCampaigns(minimal);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCampaigns([]);
  }, []);

  const handleBulkDelete = useCallback(() => {
    setDeleteConfirmKind("bulk");
  }, []);

  const handleConfirmBulkDelete = useCallback(() => {
    const campaignIds = selectedCampaigns.map((c) => c.id).filter(Boolean);
    if (campaignIds.length > 0) {
      bulkDeleteCampaigns({ campaignIds });
    }
  }, [selectedCampaigns, bulkDeleteCampaigns]);

  const isBulkDeleteModal = deleteConfirmKind === "bulk";
  const deleteModalOpen = deleteConfirmKind != null;

  const closeDeleteModal = useCallback(() => {
    setDeleteConfirmKind(null);
    setDeletingCampaignId(null);
  }, []);

  const handleRowClick = (row) => {
    navigate(`/campaigns/${encodeURIComponent(row?.id)}/analytics`);
  };

  return (
    <>
      <div className="flex flex-col px-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-[1.375rem] text-gray-800 dark:text-gray-100 font-bold">
            All Campaigns
          </h1>
          <WhatsNewBell compact className="hidden md:flex" />
        </div>
        <div className=" w-full mt-4">
          {viewState === "LOADING" && !hasSearchOrFilters ? (
            <div className="flex flex-col items-center justify-center h-[calc(100dvh-121px)] gap-3">
              <Loader text="Loading campaigns..." />
            </div>
          ) : viewState === "EMPTY_INITIAL" ? (
            <div className="w-full relative">
              <NoDataTable variant="campaigns" />
              <NoDataCommonCard
                onClick={handleNewCampaign}
                title="No Campaigns Yet"
                description="Start generating leads by creating your first campaign"
                buttonText="New Campaign"
                image={noCampaigns}
              />
            </div>
          ) : (
            <>
              <CampaignHeader
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearchChange={onSearchChange}
                isLoading={isLoading}
                displayData={displayData}
                handleNewCampaign={handleNewCampaign}
                hasSearchOrFilters={hasSearchOrFilters}
                selectedCount={selectedCampaigns.length}
                onDeleteClick={handleBulkDelete}
                onClearSelection={clearSelection}
              />
              {viewState === "LOADING" && hasSearchOrFilters && (
                <div className="flex items-center justify-center h-[calc(100dvh-121px)]">
                  <Loader text="Loading campaigns..." />
                </div>
              )}
              {(viewState === "DATA" || viewState === "EMPTY_SEARCH") && (
                <CampaignTable
                  displayData={displayData}
                  totalCount={totalCount}
                  isLoading={isLoading}
                  searchValue={searchValue}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  page={currentPage}
                  onPageChange={setCurrentPage}
                  renderCell={(campaign, columnKey) =>
                    renderCampaignCell(campaign, columnKey, actions, navigate)
                  }
                  actions={actions}
                  selectedCampaigns={selectedCampaigns}
                  setSelectedCampaigns={setSelectedCampaigns}
                  onSelectionChange={handleSelectionChange}
                  onRowClick={handleRowClick}
                  height="calc(100dvh - 136px)"
                />
              )}
            </>
          )}
        </div>
      </div>
      {showNewCampaignModal && (
        <NewCampaignModal
          isOpen={showNewCampaignModal}
          onClose={handleCloseModal}
          onImported={handleImported}
          campaignId={editingCampaignId}
        />
      )}
      {deleteModalOpen && (
        <DeleteConfirmationModal
          showConfirmDelete={deleteModalOpen}
          onClose={closeDeleteModal}
          title={
            isBulkDeleteModal
              ? `Are you sure you want to delete ${selectedCampaigns.length} campaign${selectedCampaigns.length > 1 ? "s" : ""}?`
              : "Are you sure you want to delete this campaign?"
          }
          description={
            isBulkDeleteModal
              ? "Deleting these campaigns will permanently erase your Sequences, leads, and history - This action can’t be undone."
              : "Deleting this campaign will permanently erase your Sequence, leads, and history - This action can’t be undone."
          }
          onDelete={
            isBulkDeleteModal ? handleConfirmBulkDelete : handleDeleteCampaign
          }
          deleteButtonText={
            isBulkDeleteModal ? "Delete" : "Yes, delete campaign"
          }
          cancelButtonText={isBulkDeleteModal ? "Cancel" : "I changed my mind"}
          width={isBulkDeleteModal ? undefined : "450px"}
          isDeleting={isBulkDeleteModal ? isBulkDeleting : isDeleting}
        />
      )}
    </>
  );
}
